<template>
  <div class="productProgress">
    <card-m
      :title="title"
      cardWidth="100%"
      cardHeight="100%"
      maxHeight="unset"
      toolWidth="70%"
      toolJustifyontent="flex-start"
      :show-filter="false"
      :show-export="true"
      :show-column-edit="true"
      export-file-name="产品列表"
      @export="handleExport"
      @edit-column="openColumnEdit"
    >
      <!-- 工具区：批量导入 + 批量备注 -->
      <template slot="card-tool">
        <template v-if="!batchRemarkMode">
          <el-button
            size="mini"
            icon="el-icon-upload2"
            :loading="importLoading"
            @click="handleImportClick"
          >批量导入</el-button>
          <el-button
            size="mini"
            type="primary"
            icon="el-icon-edit-outline"
            @click="enterBatchRemark"
          >批量备注</el-button>
          <el-button
            size="mini"
            icon="el-icon-warning-outline"
            @click="openIssuePage"
          >问题列表</el-button>
        </template>
        <template v-else>
          <span style="font-size: 13px; color: #409EFF;">已选 {{ selectedRows.length }} 条</span>
          <el-button
            size="mini"
            type="primary"
            :disabled="selectedRows.length === 0"
            @click="openBatchRemarkDialog"
          >填写备注</el-button>
          <el-button size="mini" @click="exitBatchRemark">退出</el-button>
        </template>
      </template>
      <!-- 表格 -->
      <template slot="card-box">
        <card-table
          ref="cardTableRef"
          tableID="productTable"
          :key="tableRenderKey"
          :total="totalCounts"
          :currentPage="currentPage"
          @sizeChange="handleSizeChanges"
          @currentChange="handleCurrentChanges"
          :tableColumn="effectiveVisibleColumns"
          :tableData="productData"
          style="height: 100%;"
          :showIndex="true"
          :showSelection="batchRemarkMode"
          @selection-change="handleSelectionChange"
          :loading="loading"
          :showPaging="true"
          :size="pageSize"
          :mobileColumns="mobileColumnEditFilter"
          @column-filter-change="onColumnFilterChange"
          :columnFilterValues="columnFilterValues"
          @column-width-change="onColumnWidthChange"
          @tag-click="goToInfo"
          @linkClick="handleSerialCodeClick"
          @textEditSave="handleTextEditSave"
        >
        </card-table>
      </template>

    </card-m>

    <!-- 批量导入：Excel 文件选择（隐藏 input，仅接收 xlsx/xls/csv） -->
    <input
      ref="excelImportInput"
      type="file"
      accept=".xlsx,.xls,.csv"
      style="display: none;"
      @change="handleImportFileChange"
    >

    <!-- 批量备注弹窗 -->
    <el-dialog
      title="批量备注"
      :visible.sync="batchRemarkDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="!batchRemarkLoading"
      @close="handleBatchRemarkDialogClose"
    >
      <div style="font-size: 13px; color: #606266; margin-bottom: 8px;">
        已选 {{ selectedRows.length }} 条产品，将统一填写以下备注内容：
      </div>
      <el-input
        v-model="batchRemarkContent"
        type="textarea"
        :rows="4"
        maxlength="500"
        show-word-limit
        placeholder="请输入备注内容（留空将清空所选产品的备注）"
      />
      <div slot="footer">
        <el-button size="mini" :disabled="batchRemarkLoading" @click="handleBatchRemarkDialogClose">取消</el-button>
        <el-button size="mini" type="primary" :loading="batchRemarkLoading" @click="confirmBatchRemark">确认备注</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入确认弹窗（第一步：后端解析匹配预览，用户确认后第二步才真正写库） -->
    <el-dialog
      title="批量导入确认"
      :visible.sync="importConfirmDialogVisible"
      width="720px"
      :close-on-click-modal="false"
      :close-on-press-escape="!importConfirmLoading"
      :show-close="!importConfirmLoading"
    >
      <div style="font-size: 13px; color: #606266; margin-bottom: 10px;">
        共解析 <b>{{ importSummary.total || 0 }}</b> 条，匹配成功
        <b style="color: #67C23A;">{{ importSummary.matched || 0 }}</b> 条，未匹配
        <b style="color: #F56C6C;">{{ importSummary.fail || 0 }}</b> 条。
        请确认信息无误后点击"确认修改"，确认后才会真正写入数据库。
      </div>
      <el-table
        :data="importPreviewList"
        size="mini"
        border
        max-height="380"
        style="width: 100%;"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="serialCode" label="产品编号" min-width="150" align="left" />
        <el-table-column prop="detail" label="备注" min-width="200" show-overflow-tooltip />
        <el-table-column label="是否匹配" width="100" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.matched" type="success" size="mini">已匹配</el-tag>
            <el-tag v-else type="danger" size="mini">未匹配</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button size="mini" :disabled="importConfirmLoading" @click="importConfirmDialogVisible = false">取消</el-button>
        <el-button size="mini" type="primary" :loading="importConfirmLoading" @click="confirmBatchImport">确认修改</el-button>
      </div>
    </el-dialog>

    <!-- 产品问题记录弹窗 -->
    <common-dialog-table
      :visible="issueDialogVisible"
      :title="'产品问题记录 - 产品ID: ' + currentProductId"
      :tableColumn="issueListColumn"
      :tableData="issueList"
      @close="closeIssueDialog"
    />

    <!-- 产品部件信息弹窗（部件/自检确认/自检记录/问题列表 四表格切换） -->
    <product-part-info-dialog
      :visible="partsDialogVisible"
      :title="'产品列表 - 产品编号: ' + currentPartsProductCode"
      :productCode="currentPartsProductCode"
      :partsList="partsList"
      :inspectionList="inspectionList"
      :resInspectionList="resInspectionList"
      :issueList="partsIssueList"
      @close="closePartsDialog"
    />

    <!-- 列编辑弹窗 -->
    <column-edit-dialog
      :visible.sync="columnEditVisible"
      :columns="tempOrderedColumns"
      :defaultColumns="effectiveDefaultColumnDefs"
      :title="columnEditTitle"
      :dialogWidth="columnEditDialogWidth"
      @confirm="handleColumnSave"
      @cancel="handleColumnCancel"
    />
  </div>
</template>

<script>
import { getProductHomeList, getProductInfoList, UpdateProductsDataList, BatchImportRemark, BatchImportConfirm } from '@/api/mainapi.js';
import UserInfoUtils from '@/utils/userInfoUtils.js';
import columnEditMixin from '@/mixins/columnEditMixin';
import ColumnEditDialog from '@/components/ColumnEditDialog.vue';
import CommonDialogTable from '@/components/card/CommonDialogTable.vue';
import ProductPartInfoDialog from '@/components/card/ProductPartInfoDialog.vue';
import FileSaver from 'file-saver';
import XLSX from 'xlsx';

/* 产品状态枚举：processStatus（产品进程）与 executeStatus（产品执行状态）共用这一套字典 */
const PRODUCT_STATUS_ENUM = {
  1: '下达',
  2: '装配中',
  3: '调试中',
  4: '检验中',
  5: '出库',
  6: '正常',
  7: '暂停',
  8: '停工',
  9: '入库中',
  10:'配料',
  11:'入库',
  12:'领料',
  13:'点料',
};
/**
 * 两个筛选下拉的取值集合不同（同一套字典，但业务上可选值不一样）：
 *   产品进程    1下达 2装配中 3调试中 4检验中 5出库 6正常 9入库中
 *   产品执行状态 1下达 6正常 7暂停 8停工
 * 注意：字典 PRODUCT_STATUS_ENUM 仍是全量（用于列表文字展示，
 *      例如产品进程返回 7 也要能显示「暂停」），只有下拉选项各自收窄。
 */
const PROCESS_STATUS_KEYS = [1, 2, 3, 4, 5, 9,10,11,12,13];
// const PROCESS_STATUS_KEYS = [1, 2, 3, 4, 5, 6, 9];
const EXECUTE_STATUS_KEYS = [1, 6, 7, 8];
/* 下拉：全部 + 各自的枚举值 */
const toStatusOptions = keys => [
  { label: '全部', value: '' },
  ...keys.map(key => ({
    label: PRODUCT_STATUS_ENUM[key],
    value: String(key)
  }))
];
const PRODUCT_PROCESS_OPTIONS = toStatusOptions(PROCESS_STATUS_KEYS);
const PRODUCT_EXECUTE_OPTIONS = toStatusOptions(EXECUTE_STATUS_KEYS);

export default {
  name: 'productProgress',
  mixins: [columnEditMixin],
  components: { ColumnEditDialog, CommonDialogTable, ProductPartInfoDialog },
  props: {
    title: {
      type: String,
      default: '产品主页'
    },
    taskNo: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // 批量备注
      batchRemarkMode: false,          // 是否处于批量备注模式（序号列变多选）
      selectedRows: [],                // 批量模式下选中的行
      batchRemarkDialogVisible: false, // 批量备注输入弹窗
      batchRemarkContent: '',          // 批量备注内容
      batchRemarkLoading: false,       // 批量保存中
      // 批量导入（两步：预览确认 → 写库）
      importLoading: false,                // 第一步请求中（解析匹配）
      importConfirmDialogVisible: false,   // 第二步预览确认弹窗
      importPreviewList: [],               // 后端解析匹配结果（list: matched/serialCode/detail）
      importSummary: {},                   // 汇总信息（total/matched/fail/status）
      importKeyId: '',                     // 后端暂存标识，确认时回传
      importConfirmLoading: false,         // 第二步确认请求中（写库）
      // 表格列定义
      productListColumn: [
        {
          prop: '数据状态码',
          label: '状态',
          width: 50,
          custom: true,
          column: {
            type: 'icon',
            icon: {
              default: 'status-circle'
            },
            tooltipField: '状态详情'
          }
        },
        {
          // 产品ID：直接用后端返回的 id 字段
          prop: 'id',
          label: '产品ID'
        },
        {
          prop: 'serialCode',
          label: '产品编号',
          filterable: true,
          custom: true,
          column: {
            type: 'link'
          }
        },
        {
          prop: 'fsn',
          label: '短代码',
          filterable: true
        },
        {
          prop: 'name',
          label: '产品名称',
          filterable: true,
          // 附加一个「不包含」关键字输入框：名称中不含有该关键字的产品才会返回
          notFilterable: true,
          filterPlaceholder: '输入包含关键字',
          notFilterPlaceholder: '输入不包含关键字'
        },
        {
          prop: 'model',
          label: '产品型号',
          filterable: true
        },
        {
          prop: 'taskNumber',
          label: '任务单号',
          filterable: true
        },
        {
          prop: 'productionDepartment',
          label: '生产车间',
          filterable: true
        },
        {
          prop: 'processStatusText',
          label: '产品进程',
          filterable: true,
          filterType: 'select',
          filterOptions: PRODUCT_PROCESS_OPTIONS
        },
        {
          prop: 'executeStatusText',
          label: '产品执行状态',
          filterable: true,
          filterType: 'select',
          filterOptions: PRODUCT_EXECUTE_OPTIONS
        },
        {
          prop: 'createTime',
          label: '创建时间',
          filterable: true,
          // ✅ 改成了「时间段」筛选：card-table 的 date 类型会把 [开始, 结束] 放进 filterValues
          filterType: 'date'
        },
        {
          prop: 'parameter',
          label: '调试参数',
          filterable: true
        },
        {
          prop: 'detail',
          label: '备注',
          filterable: true,
          custom: true,
          column: {
            type: 'textEdit',
            field: 'detail',
            maxDisplayLength: 10
          }
        },
        {
          prop: 'isIssue',
          label: '有无问题',
          filterable: true,
          filterType: 'select',
          filterOptions: [
            { label: '全部', value: '' },
            { label: '有问题', value: 'true' },
            { label: '无问题', value: 'false' }
          ],
          custom: true,
          column: {
            type: 'tag',
            clickable: true,
            tag: {
              'true': { text: '有', type: 'danger' },
              'false': { text: '无', type: 'success' }
            }
          }
        }

      ],

      // 手机端可用列 prop 过滤（供 mixin 保存/加载手机端列偏好）
      mobileColumnFilter: [
        '数据状态码',
        'serialCode',
        'name',
        'model',
        'processStatusText',
        'executeStatusText',
        'isIssue',
        'createTime',
        'parameter',
        'detail'
      ],
      // 手机端列编辑弹窗中展示的列（可编辑范围 > 默认显示范围）
      mobileColumnEditFilter: [
        '数据状态码',
        'id',
        'serialCode',
        'fsn',
        'name',
        'model',
        'taskNumber',
        'processStatusText',
        'executeStatusText',
        'isIssue',
        'createTime',
        'parameter',
        'detail'
      ],

      // 列筛选条件
      serialCodeKeyword: '',
      nameKeyword: '',
      nameNotKeyword: '',   // 产品名称「不包含」关键字（传后端 NameNot）
      modelKeyword: '',
      processStatusKeyword: '',  // 产品进程筛选（传枚举数字）
      executeStatusKeyword: '',  // 产品执行状态筛选（传枚举数字）
      fsnKeyword: '',
      taskNumberKeyword: '',
      isIssueKeyword: '',
      parameterKeyword: '',
      detailKeyword: '',
      productionDepartmentKeyword: '',  // ✅ 生产车间（新接口入参 productionDepartment）
      // ✅ 创建时间由「单个日期」改为「时间段」：[开始, 结束]，未选时为 []
      createTimeRange: [],
      columnFilterValues: {},

      // 分页
      currentPage: 1,
      pageSize: 50,

      // 数据
      productData: [],
      totalCounts: 0,
      loading: false,

      // 产品问题记录弹窗
      issueDialogVisible: false,
      issueList: [],
      issueListColumn: [
        { prop: 'create_time', label: '创建时间' },
        { prop: 'operation_person_name', label: '人员' },
        { prop: 'SHOWVALUE', label: '类型' },
        { prop: 'field0002', label: '问题' },
        { prop: 'detail', label: '备注' }
      ],
      currentProductId: '',

      // 产品部件信息弹窗（部件/自检确认/自检记录/问题列表 四表格切换）
      partsDialogVisible: false,
      partsList: [],
      inspectionList: [],
      resInspectionList: [],
      partsIssueList: [],
      currentPartsProductCode: '',

      // 列偏好页面标识（mixin 需要）
      pageType: 'productProgress'
    };
  },
  computed: {
    // 按用户偏好顺序排列的列定义
    // 供 mixin 使用的列定义
    baseColumnDefs() {
      return this.productListColumn;
    }
  },
  async mounted() {
    await this.loadColumnPref();
    this.getProductList();
  },
  methods: {
    /** 获取产品列表
     *  ✅ 新接口：请求体为扁平结构（不再包 Type/Page/PageSize/Body.data），
     *     分页改 pageNo / pageSize；创建时间改为时间段 productCreateTimeStart / productCreateTimeEnd。 */
    async getProductList() {
      this.loading = true;

      // ✅ 创建时间段：card-table 的 filterType:'date' 给的是 [开始, 结束]（yyyy-MM-dd）
      const range = Array.isArray(this.createTimeRange) ? this.createTimeRange : [];
      const productCreateTimeStart = range[0] || '';
      const productCreateTimeEnd = range[1] || '';

      const body = {
        pageNo: String(this.currentPage),
        pageSize: String(this.pageSize),

        serialCode: this.serialCodeKeyword || '',
        fsn: this.fsnKeyword || '',
        name: this.nameKeyword || '',
        nameNot: this.nameNotKeyword || '',        // 名称中不包含该关键字
        model: this.modelKeyword || '',
        taskNo: this.taskNumberKeyword || '',      // 任务单号 → taskNo
        processStatus: this.processStatusKeyword || '',   // 产品进程（枚举数字字符串）
        executeStatus: this.executeStatusKeyword || '',   // 产品执行状态（枚举数字字符串）
        productCreateTimeStart,
        productCreateTimeEnd,
        parameter: this.parameterKeyword || '',
        productDetail: this.detailKeyword || '',   // 备注 → productDetail
        productionDepartment: this.productionDepartmentKeyword || '',
        isIssue: this.isIssueKeyword || ''
      };

      try {
        const res = await getProductHomeList(body);

        this.loading = false;

        if (res.data.code === 200) {
          // ✅ 新返回：data.productList.{ total, list }
          const productList = (res.data.data) || {};
          this.totalCounts = productList.total || 0;
          this.productData = this.processProductData(productList.productList || []);
        } else {
          this.$message.error(res.data.message || '获取产品列表失败');
        }
      } catch (error) {
        this.loading = false;
        this.$message.error('网络错误，请检查连接');
      }
    },

    /** 处理产品数据
     *  ✅ 新接口的字段名改成了下划线（serial_code / create_time / process_status / task_number …），
     *     这里统一映射回表格列 prop，保证「之前有什么列还显示什么」。
     *     ⚠️ 同时对老字段名做了兜底（item.xxx || item.xxx_xxx），万一后端回滚旧字段也不会全空。 */
    processProductData(list) {
      return list.map(item => {
        // 进程 / 执行状态取值：优先新接口的下划线字段，回退老驼峰字段
        const processStatus = item.process_status !== undefined ? item.process_status : item.processStatus;
        const executeStatus = item.execute_status !== undefined ? item.execute_status : item.executeStatus;
        const createTimeRaw = item.create_time !== undefined ? item.create_time : item.createTime;

        // 产品进程 / 产品执行状态：共用同一套枚举（1下达 … 9入库中）
        const processText = PRODUCT_STATUS_ENUM[processStatus] || '';
        const executeText = PRODUCT_STATUS_ENUM[executeStatus] || '';
        // 整体状态小圆点：仍按 status 走（0异常 / 1生产中 / 2已完成）
        let 数据状态码 = '';
        if (item.status === 1) {
          数据状态码 = 2;
        } else if (item.status === 2) {
          数据状态码 = 1;
        } else if (item.status === 0) {
          数据状态码 = 0;
        }

        // 新接口的 isIssue 是字符串 "true" / "false"，统一成字符串，保证 tag 列正确映射成 有 / 无
        let isIssue = item.isIssue;
        if (typeof isIssue === 'boolean') isIssue = isIssue ? 'true' : 'false';

        return {
          id: item.id === undefined || item.id === null ? '' : item.id, // 产品ID
          serialCode: item.serial_code || item.serialCode || '',
          fsn: item.fsn || '',
          name: item.name || '',
          model: item.model || '',
          taskNumber: item.task_number || item.taskNumber || '',
          productionDepartment: item.productionDepartment || '',   // 生产车间（新接口同名）
          processStatusText: processText,   // 产品进程
          executeStatusText: executeText,   // 产品执行状态
          数据状态码: 数据状态码,
          isIssue: isIssue,
          // 悬浮提示：进程 + 执行状态
          '状态详情': [processText, executeText].filter(Boolean).join(' / ') || '未知',
          createTime: this.formatDate(createTimeRaw),   // 截断到 yyyy-MM-dd
          parameter: item.parameter || '',
          // 备注：新接口样本里没返回 detail，先用 productDetail 兜底，两者都没有就空
          detail: item.detail || item.productDetail || '',
          // 保留原始数据供后续使用
          _raw: item
        };
      });
    },

    /** 列筛选变化 */
    onColumnFilterChange(filterValues) {
      this.serialCodeKeyword = filterValues['serialCode'] || '';
      this.nameKeyword = filterValues['name'] || '';
      // 产品名称「不包含」关键字：card-table 存于 filterValues['name__not']
      this.nameNotKeyword = filterValues['name__not'] || '';
      this.modelKeyword = filterValues['model'] || '';
      this.processStatusKeyword = filterValues['processStatusText'] || '';
      this.executeStatusKeyword = filterValues['executeStatusText'] || '';
      this.fsnKeyword = filterValues['fsn'] || '';
      this.taskNumberKeyword = filterValues['taskNumber'] || '';
      this.isIssueKeyword = filterValues['isIssue'] || '';
      this.parameterKeyword = filterValues['parameter'] || '';
      this.detailKeyword = filterValues['detail'] || '';
      // ✅ 生产车间（列定义里是 filterable，之前忘了读，新接口有这个入参，补上）
      this.productionDepartmentKeyword = filterValues['productionDepartment'] || '';
      // ✅ 创建时间：filterType:'date' 给的是 [开始, 结束]；card-table 未选时给 ''
      const range = filterValues['createTime'];
      this.createTimeRange = Array.isArray(range) && range.length === 2 ? range : [];
      this.columnFilterValues = filterValues;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.getProductList();
    },

    /** 分页 */
    handleSizeChanges(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.getProductList();
    },

    handleCurrentChanges(val) {
      this.currentPage = val;
      this.getProductList();
    },

    /** 导出Excel */
    handleExport() {
      const xlsxParam = { raw: true };
      const wb = XLSX.utils.table_to_book(
        document.querySelector('#productTable'),
        xlsxParam
      );
      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'array'
      });
      try {
        FileSaver.saveAs(
          new Blob([wbout], { type: 'application/octet-stream' }),
          '产品列表.xlsx'
        );
      } catch (e) {
        // 导出失败时静默处理，避免在无 console 环境报错
      }
      return wbout;
    },

    /** 产品列表行点击 - 查询问题记录 */
    async goToInfo(data) {
      const rowId = data._raw && data._raw.id;
      if (!rowId) {
        // this.$message.info('该产品没有问题记录');
        return;
      }

      this.currentProductId = rowId;

      try {
        const params = {
          Type: 'ProductIssue',
          msg: rowId.toString()
        };

        const res = await getProductInfoList(params);

        if (res.data.code === 200) {
          if (res.data.data.issue === '空' || !res.data.data.issue || res.data.data.issue.length === 0) {
            // this.$message.info('该产品没有问题记录');
          } else {
            this.issueList = res.data.data.issue;
            this.issueDialogVisible = true;
          }
        } else {
          this.$message.error('查询问题记录失败: ' + res.data.message);
        }
      } catch (error) {
        this.$message.error('查询问题记录失败');
      }
    },

    /** 关闭问题记录弹窗 */
    closeIssueDialog() {
      this.issueDialogVisible = false;
      this.issueList = [];
      this.currentProductId = '';
    },

    /** 产品编号链接点击 - 查询部件信息（部件/自检确认/自检记录） */
    async handleSerialCodeClick(payload) {
      const row = payload.row;
      const serialCode = row.serialCode || (row._raw && row._raw.serialCode);
      if (!serialCode) return;

      this.currentPartsProductCode = serialCode;

      try {
        const params = {
          Type: 'ProductInformation',
          msg: serialCode
        };

        const res = await getProductInfoList(params);

        if (res.data.code === 200) {
          const data = res.data.data || {};
          const parts = data.parts || [];
          const inspection = data.inspectionList || [];
          const resInspection = data.resInspectionList || [];
          // 后端无问题时 issue 可能为字符串 '空'，做兼容处理
          const issue = Array.isArray(data.issue) ? data.issue : [];

          if (parts.length > 0 || inspection.length > 0 || resInspection.length > 0 || issue.length > 0) {
            this.partsList = parts;
            this.inspectionList = inspection;
            this.resInspectionList = resInspection;
            this.partsIssueList = issue;
            this.partsDialogVisible = true;
          } else {
            this.$message.info('该产品没有详细信息');
          }
        } else {
          this.$message.error('查询部件信息失败: ' + res.data.message);
        }
      } catch (error) {
        this.$message.error('查询部件信息失败');
      }
    },

    /** 关闭部件信息弹窗 */
    closePartsDialog() {
      this.partsDialogVisible = false;
      this.partsList = [];
      this.inspectionList = [];
      this.resInspectionList = [];
      this.partsIssueList = [];
      this.currentPartsProductCode = '';
    },

    /** 备注列保存（inline 编辑） */
    async handleTextEditSave(data) {
      const userCode = this.getCurrentUserCode();
      if (!userCode) return;

      // msg 传该行产品的 id（优先取行数据，兜底取 _raw），转数字类型避免带引号
      const rawId = data.row.id !== undefined && data.row.id !== null
        ? data.row.id
        : (data.row._raw && data.row._raw.id);
      const productId = Number(rawId);
      if (rawId === undefined || rawId === null || rawId === '' || isNaN(productId)) {
        this.$message.warning('产品ID不能为空');
        return;
      }

      try {
        const res = await UpdateProductsDataList({
          msg: productId,
          data: {
            detail: data.value || '',
            update_by: userCode
          }
        });

        if (res.data.code === 200) {
          this.$message.success('备注保存成功');
          // 前端同步：必须用本行 data.row（与表格行是同一对象引用）。
          // 表格客户端排序后 data.index 是显示位置下标，与 productData 原始顺序不一致，用 index 会改到其他行
          if (data.row) {
            this.$set(data.row, 'detail', data.value);
            if (data.row._raw) {
              this.$set(data.row._raw, 'detail', data.value);
            }
          }
        } else {
          this.$message.error('保存失败：' + (res.data.message || '未知错误'));
        }
      } catch (err) {
        this.$message.error('网络错误，请检查连接');
      }
    },

    /** 跳转到产品问题列表页面 /homepage/products/issueinfo */
    openIssuePage() {
      this.$router.push('/homepage/products/issueinfo');
    },

    /** 进入批量备注模式：序号列变为多选列 */
    enterBatchRemark() {
      this.batchRemarkMode = true;
      this.selectedRows = [];
      this.batchRemarkContent = '';
    },

    /** 退出批量备注模式：恢复序号列并清空选择 */
    exitBatchRemark() {
      this.batchRemarkMode = false;
      this.selectedRows = [];
      this.batchRemarkContent = '';
      if (this.$refs.cardTableRef) {
        this.$refs.cardTableRef.clearSelection();
      }
    },

    /** 多选变化 */
    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },

    /** 打开批量备注输入弹窗 */
    openBatchRemarkDialog() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先勾选需要备注的产品');
        return;
      }
      this.batchRemarkDialogVisible = true;
    },

    /** 关闭批量备注弹窗（保存中不允许关闭） */
    handleBatchRemarkDialogClose() {
      if (this.batchRemarkLoading) return;
      this.batchRemarkDialogVisible = false;
    },

    /** 批量保存备注：逐条调用单条备注接口，统计成功条数 */
    async confirmBatchRemark() {
      const userCode = this.getCurrentUserCode();
      if (!userCode) return;

      // 备注内容允许为空：留空即清空所选产品的备注
      const content = (this.batchRemarkContent || '').trim();
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先勾选需要备注的产品');
        return;
      }

      this.batchRemarkLoading = true;
      let successCount = 0;
      let failCount = 0;

      for (const row of this.selectedRows) {
        // msg 传该行产品的 id（优先取行数据，兜底取 _raw），转数字类型避免带引号
        const rawId = row.id !== undefined && row.id !== null
          ? row.id
          : (row._raw && row._raw.id);
        const productId = Number(rawId);
        if (rawId === undefined || rawId === null || rawId === '' || isNaN(productId)) {
          failCount++;
          continue;
        }
        try {
          const res = await UpdateProductsDataList({
            msg: productId,
            data: {
              detail: content,
              update_by: userCode
            }
          });
          if (res.data.code === 200) {
            successCount++;
            // 前端同步该行备注
            this.$set(row, 'detail', content);
            if (row._raw) {
              this.$set(row._raw, 'detail', content);
            }
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }

      this.batchRemarkLoading = false;

      if (successCount > 0 && failCount > 0) {
        this.$message.warning(`成功备注 ${successCount} 条，失败 ${failCount} 条`);
      } else if (successCount > 0) {
        this.$message.success(`成功备注 ${successCount} 条`);
      } else {
        this.$message.error(`备注失败 ${failCount} 条`);
      }

      this.batchRemarkDialogVisible = false;
      this.batchRemarkContent = '';
      this.exitBatchRemark();
    },

    /** 批量导入：触发隐藏文件选择 */
    handleImportClick() {
      this.$refs.excelImportInput.value = '';
      this.$refs.excelImportInput.click();
    },

    /** 选择文件后：Excel → CSV → BatchImportRemark（第一步：后端解析匹配，不写库）→ 弹窗预览确认 */
    async handleImportFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      this.importLoading = true;
      try {
        // 1. 读取文件为 ArrayBuffer
        const buffer = await this.readFileAsArrayBuffer(file);
        // 2. xlsx 解析（编号列按文本读取，避免 000 开头被丢 0）
        const wb = XLSX.read(buffer, { type: 'array', cellText: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        // 3. 转 CSV（仅保留"编号"+"备注"两列，编号转文本）
        const csv = this.excelToCsv(ws);
        if (!csv) {
          this.$message.warning('Excel 内容为空或格式不正确');
          return;
        }
        // 4. 第一步：调用解析匹配接口（后端不写库，仅返回暂存标识与预览数据）
        const userCode = this.getCurrentUserCode();
        if (!userCode) return;
        const res = await BatchImportRemark({
          detail: csv,
          updateBy: userCode
        });
        if (res.data.code === 200 && res.data.data) {
          const data = res.data.data;
          // 保存暂存标识与预览数据，弹窗供用户确认
          this.importKeyId = data.keyId || '';
          this.importPreviewList = (data.list || []).map(item => ({
            serialCode: item.serialCode || '',
            detail: item.detail || '',
            matched: item.matched === true || item.matched === 'true'
          }));
          this.importSummary = {
            total: data.total || this.importPreviewList.length,
            matched: this.importPreviewList.filter(item => item.matched).length,
            fail: data.fail || this.importPreviewList.filter(item => !item.matched).length,
            status: data.status
          };
          if (!this.importKeyId) {
            this.$message.warning('后端未返回暂存标识（keyId），无法确认导入');
          }
          this.importConfirmDialogVisible = true;
        } else {
          this.$message.error('导入解析失败：' + (res.data.message || '未知错误'));
        }
      } catch (err) {
        this.$message.error('导入失败，请检查 Excel 文件格式');
      } finally {
        this.importLoading = false;
        e.target.value = '';
      }
    },

    /** 第二步：用户点击"确认修改"后，调用写库接口（confirm: true） */
    async confirmBatchImport() {
      const userCode = this.getCurrentUserCode();
      if (!userCode) return;
      if (!this.importKeyId) {
        this.$message.error('缺少暂存标识（keyId），请重新导入');
        return;
      }
      this.importConfirmLoading = true;
      try {
        const res = await BatchImportConfirm({
          keyId: this.importKeyId,
          confirm: 'true',
          updateBy: userCode
        });
        if (res.data.code === 200) {
          const data = res.data.data || {};
          this.importConfirmDialogVisible = false;
          this.$message.success(data.msg || '导入完成');
          this.resetImportState();
          // 备注可能已更新，刷新列表
          this.getProductList();
        } else {
          this.$message.error('导入失败：' + (res.data.message || '未知错误'));
        }
      } catch (err) {
        this.$message.error('导入失败，请检查网络连接');
      } finally {
        this.importConfirmLoading = false;
      }
    },

    /** 清空批量导入状态 */
    resetImportState() {
      this.importPreviewList = [];
      this.importSummary = {};
      this.importKeyId = '';
      this.importConfirmLoading = false;
    },

    /** File → ArrayBuffer */
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },

    /**
     * 工作表 → 管道分隔文本（| 分隔）
     * 要求：第一行为表头，包含"编号"（对应 serialCode）和"备注"（对应 detail）。
     * 编号列一律按文本处理（String()），避免 000 开头的编号被删 0。
     */
    excelToCsv(ws) {
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
      if (!rows || rows.length === 0) return '';
      const header = rows[0].map((h) => String(h == null ? '' : h).trim());
      const codeIdx = header.findIndex((h) => h === '编号' || h === '产品编号' || h.toLowerCase() === 'serialcode');
      const remarkIdx = header.findIndex((h) => h === '备注' || h.toLowerCase() === 'detail');
      if (codeIdx === -1) {
        this.$message.error('Excel 中缺少"编号"列（表头需包含"编号"或"产品编号"）');
        return '';
      }
      if (remarkIdx === -1) {
        this.$message.error('Excel 中缺少"备注"列（表头需包含"备注"）');
        return '';
      }
      const outRows = [['编号', '备注']];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row) continue;
        const code = row[codeIdx] == null ? '' : String(row[codeIdx]).trim();
        const remark = row[remarkIdx] == null ? '' : String(row[remarkIdx]);
        if (code === '') continue; // 跳过空行
        outRows.push([code, remark]);
      }
      return this.buildCsv(outRows);
    },

    /** 二维数组 → 管道分隔文本（| 分隔，处理 |/引号/换行转义） */
    buildCsv(rows) {
      return rows
        .map((row) =>
          row
            .map((cell) => {
              const s = String(cell == null ? '' : cell);
              return /[|"\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
            })
            .join('|')
        )
        .join('\n');
    },

    /** 日期格式化：取前 10 位（yyyy-MM-dd） */
    formatDate(val) {
      if (!val) return '';
      const str = String(val);
      return str.length >= 10 ? str.substring(0, 10) : str;
    },

    // 获取当前用户工号
    getCurrentUserCode() {
      const userCode = UserInfoUtils.getUserCode();
      if (!userCode) {
        this.$message.warning('未获取到用户信息，请重新登录');
        this.$router.push('/login');
        return null;
      }
      return userCode;
    }
  }
};
</script>

<style scoped lang="scss">
.productProgress {
  width: 100%;
  height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
}

::v-deep .card-m {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

::v-deep .card-box {
  flex: 1;
  min-height: 0;
  overflow-y: hidden;
  overflow-x: hidden;
}

/* 手机端 */
@media (max-width: 768px) {
  .productProgress {
    height: calc(100vh - 50px);
  }
}

</style>
